import { EM_DASH, EMPTY_CELL_HYPHEN } from "constants/index";

import { VFC } from "react";

import { BrokerShipment } from "api/rest/shipments/types";
import ColumnListView, { ColumnListViewProps } from "components/ColumnListView";
import AllFilePreview from "components/file-uploads/AllFilePreview";
import FileList from "components/file-uploads/FileList";
import { useFilesContext } from "components/file-uploads/FilesControl";
import PortexDrawer from "components/PortexDrawer";
import SummaryPaperView from "components/SummaryPaperView";
import CargoGroupsOverviewView from "features/cargo-groups/views/CargoGroupsOverviewView";
import { ShipmentCargoDetailsView } from "features/shipments/cargo-details/ShipmentCargoDetailsView";
import { mapDriverPreferenceToCopy } from "pages/shipper/utils/mapDriverPreferenceToCopy";
import { useTranslation } from "react-i18next";
import { formatUSD } from "utils/formatCurrency";
import VerticalStopsInfo from "views/stops/VerticalStopsInfo";

type Section = Pick<ColumnListViewProps, "title" | "rows">;

interface BrokerShipmentDetailsViewProps {
  shipment: BrokerShipment;
}

const BrokerShipmentDetailsView: VFC<BrokerShipmentDetailsViewProps> & { Drawer: typeof Drawer } = ({ shipment }) => {
  const { t } = useTranslation(["loads", "shipper", "common"]);

  const cargoGroups = shipment.trucks.map((truck) => truck.cargo_groups ?? []).flat();

  const sections: Section[] = [
    {
      rows: [
        {
          label: t("loads:viewAllDetails_labels_quoteNumber"),
          value: shipment.quote_number ?? EM_DASH,
          hide: !shipment.quote_number,
        },
        {
          label: t("loads:viewAllDetails_labels_ref"),
          value: shipment.reference_number,
        },
        {
          label: t("loads:viewAllDetails_labels_internalRef"),
          value: shipment.internal_reference_number || EMPTY_CELL_HYPHEN,
        },
        {
          label: t("loads:viewAllDetails_labels_additionalNotes"),
          value: shipment.additional_notes || EMPTY_CELL_HYPHEN,
        },
        {
          label: t("loads:quotedAmount"),
          value: shipment.quoted_amount ? formatUSD(shipment.quoted_amount) : EM_DASH,
        },
        {
          label: t("loads:viewAllDetails_labels_shipper"),
          value: shipment.shipper_name,
        },
        {
          label: t("loads:viewAllDetails_labels_mode"),
          value: shipment.mode,
        },
        {
          label: t("loads:viewAllDetails_labels_numOfLoads"),
          value: shipment.truck_quantity,
        },
        {
          label: t("loads:viewAllDetails_labels_loads"),
          value: (
            <ColumnListView
              rows={shipment.trucks.map((load, index, loads) => ({
                label: t("loads:viewAllDetails_labels_load", { position: index + 1 }),
                value: <span className="font-normal">{load.reference_number || EMPTY_CELL_HYPHEN}</span>,
                hide: loads.length === 1,
                hideDivider: true,
                BoxProps: { style: { padding: 0 } },
              }))}
            />
          ),
        },
        {
          label: t("loads:viewAllDetails_labels_equipment"),
          value: shipment.equipment,
        },
        {
          label: t("loads:viewAllDetails_labels_driverPref"),
          value: mapDriverPreferenceToCopy[shipment.load_spec.driver_preference || "NONE"],
        },
        {
          label: t("common:unloadingDetails"),
          value: shipment.unloading_details
            ? t(`common:unloadingDetails_map.${shipment.unloading_details}`)
            : t("common:na"),
          hide: !shipment.unloading_details,
        },
        {
          label: t("loads:viewAllDetails_labels_lane"),
          value: shipment.lane,
        },
        {
          label: t("loads:viewAllDetails_labels_miles"),
          value: shipment.miles ?? 0,
          hide: !shipment.miles,
        },
        {
          label: t("loads:viewAllDetails_labels_locations"),
          value: <VerticalStopsInfo.BrokerShipmentStops stops={shipment.stops} />,
        },
        {
          label: t("loads:weight", { unit: shipment.load_spec.weight_unit }),
          value: shipment.load_spec.total_weight ?? EM_DASH,
          hide: shipment.load_spec.total_weight === undefined,
        },
        {
          label: t("common:length"),
          value: shipment.load_spec.length_per_unit ?? EM_DASH,
          hide: shipment.load_spec.length_per_unit === undefined || shipment.load_spec.length_per_unit === null,
        },
        {
          label: t("loads:commodities"),
          value: shipment.load_spec.commodities?.split("loads:,").join(", "),
        },
        {
          label: t("loads:palletCount"),
          value: shipment.load_spec.pallet_count ?? EM_DASH,
          hide: shipment.load_spec.pallet_count === undefined,
        },
        {
          label: t("loads:caseCount"),
          value: shipment.load_spec.case_count ?? EM_DASH,
          hide: shipment.load_spec.case_count === undefined,
        },
        {
          label: t("loads:palletType"),
          value: shipment.load_spec.pallet_type
            ? t(`shipper:palletTypeMap.${shipment.load_spec.pallet_type}`)
            : EM_DASH,
          hide: shipment.load_spec.pallet_type === undefined,
        },
        {
          label: t("loads:packingType"),
          value: shipment.load_spec.packaging_type
            ? t(`shipper:packingTypeMap.${shipment.load_spec.packaging_type}`)
            : EM_DASH,
          hide: shipment.load_spec.packaging_type === undefined,
        },
        {
          label: t("shipper:packingCount"),
          value: shipment.load_spec.packaging_count ?? EM_DASH,
          hide: shipment.load_spec.packaging_count === undefined,
        },
        {
          label: t("common:cargoDetails"),
          value: <ShipmentCargoDetailsView package_groups={shipment.load_spec.package_groups ?? []} />,
          hide: shipment.mode !== "LTL",
          BoxProps: { style: { flexDirection: "column" } },
        },
        {
          label: t("common:attachments"),
          value: (
            <FileList FileListViewProps={{ CollapseProps: { style: { overflow: "hidden" } }, disableDelete: true }} />
          ),
          ContetTypographyProps: { style: { overflow: "hidden" } },
        },
        {
          label: t("common:handlingUnits"),
          value: <CargoGroupsOverviewView cargoGroups={cargoGroups} variant="handlingUnit" />,
          hide: cargoGroups.length === 0,
        },
      ],
    },
  ];

  return (
    <div className="p-4 gap-4 flex flex-col">
      {sections.map((section, index) => (
        <SummaryPaperView
          key={`section_${index}_${section.title}`}
          PaperProps={{
            variant: "outlined",
            style: { paddingTop: 0, paddingBottom: 8 },
          }}
          ColumnListViewProps={{
            title: section.title && <span className="uppercase text-brandBlue">{section.title}</span>,
            rows: section.rows.map((item) => ({
              ...item,
              label: <span className="font-bold">{item.label}</span>,
              value: <span className="font-normal whitespace-pre-wrap">{item.value || EMPTY_CELL_HYPHEN}</span>,
              BoxProps: { style: { paddingTop: 6, paddingBottom: 6 }, ...(item.BoxProps || {}) },
            })),
          }}
        />
      ))}
    </div>
  );
};

interface DrawerProps extends BrokerShipmentDetailsViewProps {
  isOpen: boolean;
  onClose: () => void;
}

const Drawer: VFC<DrawerProps> = (props) => {
  const { t } = useTranslation("loads");
  const { isOpen, onClose, ...brokerShipmentDetailsViewProps } = props;
  const { useGetFiles } = useFilesContext();
  const files = useGetFiles();

  const handleClose = () => {
    onClose();
  };

  if (files.length === 0) {
    return (
      <PortexDrawer
        headerTitle={t("viewAllDetails_drawerHeaderTitle")}
        width={"50%"}
        open={isOpen}
        onClose={handleClose}
      >
        <div className="w-full h-full flex flex-row">
          <div className="w-full flex flex-col overflow-y-auto">
            <BrokerShipmentDetailsView {...brokerShipmentDetailsViewProps} />
          </div>
        </div>
      </PortexDrawer>
    );
  }

  return (
    <PortexDrawer headerTitle={t("viewAllDetails_drawerHeaderTitle")} width={"90%"} open={isOpen} onClose={handleClose}>
      <div className="w-full h-full flex flex-row">
        <div className="w-2/3">
          <AllFilePreview />
        </div>
        <div className="w-1/3 flex flex-col overflow-y-auto">
          <BrokerShipmentDetailsView {...brokerShipmentDetailsViewProps} />
        </div>
      </div>
    </PortexDrawer>
  );
};

BrokerShipmentDetailsView.Drawer = Drawer;

export default BrokerShipmentDetailsView;
